/* eslint-disable react/require-default-props */
// @flow
import React from 'react';
import type { ChildrenArray, Element, Node } from 'react';
import styled, { css } from 'styled-components';
import type { Interpolation } from 'styled-components';
import type Cell from './Cell';
import type { AreaAssignment } from './NamedGridLayout';
import NamedGridLayout from './NamedGridLayout';
import SimpleGridLayout from './SimpleGridLayout';
import spacingUnits from './spacing';

/**
 * The actual work of rendering a grid layout is performed by classes
 * implementing this interface.
 */
export interface GridLayout {
  /**
   * The value of the `grid-template` CSS prop.
   */
  +templateSpec: string;
  /**
   * A set of child rules (e.g. using `> * { ... }`) that can be applied
   * from the Grid component.
   */
  +childRules: string;
  /**
   * How to render the Grid component's children.
   */
  +children: Node;
}
type Props = {
  gridLayout: GridLayout,
  rowSpacing: string,
  columnSpacing: string,
};
const StyledGrid = styled.div`
  display: grid;
  grid-template: ${({ gridLayout }) => gridLayout.templateSpec};
  ${({ rowSpacing, columnSpacing }: Props) => css`
    grid-gap: ${rowSpacing ? spacingUnits[rowSpacing] : 0}
      ${columnSpacing ? spacingUnits[columnSpacing] : 0};
  `};
  ${({ gridLayout }) => gridLayout.childRules};
`;

type ElementNode = Element<any> | ChildrenArray<Element<any>>;

type GridTemplateString = string | Array<Interpolation>;

// The following represents the preferred prop-type we would like to use, but
// flow refinement does not yet work as expected with distinguishing type
// unions based on key type - see https://github.com/facebook/flow/issues/4772
/*
type CommonGridProps = {|
  rowSpacing?: string,
  columnSpacing?: string,
|};
type GridProps = (
  {|
    layout: GridTemplateString,
    children: AreaAssignment,
    ...CommonGridProps,
  |} | {|
    row: true,
    children: Iterable<Element<Cell>>,
    ...CommonGridProps,
  |} | {|
    column: true,
    children: Iterable<Element<Cell>>,
    ...CommonGridProps,
  |} | {|
    row: GridTemplateString,
    children: ElementNode,
    ...CommonGridProps,
  |} | {|
    column: GridTemplateString,
    children: ElementNode,
    ...CommonGridProps,
  |}
)
*/
// This is a compromise type definition for Grid props (see above)
type GridProps = {
  /**
   * A valid {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template
   * CSS `grid-template` string} with named areas.
   *
   * Note that we prefer putting the column sizes on their own line beneath
   * the rows so they visually align to the columns they size in the "ASCII-art"
   * grid area definition.
   */
  layout?: GridTemplateString,
  /**
   * Displays the children as a "simple", single row horizontal grid.
   *
   * If the value is the boolean `true`, the size of each cell in the row
   * is expected to be set by child `Cell` components with a `size` prop.
   *
   * If the value is a string, it is expected to be a valid
   * A {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-rows
   * CSS `grid-template-columns` string} (columns rather than rows since
   * each "cell" is, in fact, a column within the row) where each space
   * separated size will apply to each of Grid's children in their
   * respective order.
   */
  row?: true | GridTemplateString,
  /**
   * Displays the children as a "simple", single column vertical grid.
   *
   * If the value is the boolean `true`, the size of each cell in the column
   * is expected to be set by child `Cell` components with a `size` prop.
   *
   * If the value is a string, it is expected to be a valid
   * A {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-rows
   * CSS `grid-template-rows` string} (rows rather than columns since
   * each "cell" is, in fact, a row within the column) where each space
   * separated size will apply to each of Grid's children in their
   * respective order.
   */
  column?: true | GridTemplateString,
  /**
   * The Grid content cells. They must be passed in different formats
   * depending on the type of Grid layout being used.
   *
   * For full Grid layouts (using the `layout` prop), Grid's child must be a
   * single object, mapping React elements to their Grid area names.
   *
   * For simple row or column Grid layouts with string values, you only need to
   * pass in one child for each grid size. If the number of children does
   * not match the number of sizes specified, regular {@link
   * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout/Auto-placement_in_CSS_Grid_Layout
   * CSS Grid auto-placement rules} apply.
   *
   * For simple row or column Grid layouts with boolean `true` values, each
   * child *must* be a `<Cell />` element with a `size` prop.
   */
  children: AreaAssignment | ElementNode | Iterable<Element<Cell>>,
  /**
   * Sets spacing between row grid tracks.
   */
  rowSpacing?: string,
  /**
   * Sets spacing between column grid tracks.
   */
  columnSpacing?: string,
};

function getSimpleLayoutSpec(spec: string | true, children: *): string {
  if (typeof spec === 'string') {
    return spec;
  }
  const childArr = React.Children.toArray(children);
  const sizeFilterFn = child => typeof child.props.size !== 'string';
  if (childArr.some(sizeFilterFn)) {
    // eslint-disable-next-line no-console
    console.error('Invalid dynamic Grid children:', childArr.filter(sizeFilterFn));
    throw new Error('Invalid dynamic Grid child is missing a size - see log for details');
  }
  return childArr.map(child => child.props.size).join(' ');
}

/**
 * Lays out its children in a grid (using CSS Grids).
 *
 * There are two types of grid layouts you can use: full grid layouts (which
 * specify a set of rows and columns), and simple layouts (which are a
 * single row or column).
 *
 * Full grid layouts must be specified in the `layout` prop using standard
 * `grid-template` syntax (the one that makes everything look a bit like
 * ASCII-art):
 *
 * @example
 * <Grid layout="
 *   ' A    A   A ' 20px
 *   ' B    B   C ' auto
 *   ' D    D   C ' max-content
 *   / 1fr  3fr 2fr
 * ">{{
 *  A: <ComponentA />,
 *  B: <ComponentB />,
 *  C: <ComponentC />,
 *  D: <ComponentD />,
 * }}</Grid>
 *
 * Note that child component are *assigned* to *named areas* (in the above
 * case `A`, `B`, `C` and `D`) instead of simply rendered as regular
 * children. Just like with standard CSS named area grids, the names need
 * not be single letters - they just need to match the keys of the assignment.
 *
 * Unlike regular CSS named area grids however, you need not worry about
 * setitng the `grid-area` CSS property for each child (the Grid component
 * will do it for you).
 *
 *
 * For creating simple grid layouts (single rows or column), you would
 * instead use the `row` or `column` properties:
 *
 * @example
 * <Grid row="1fr 3fr 2fr">
 *   <ComponentA />
 *   <ComponentB />
 *   <ComponentC />
 * </Grid>
 *
 * This will simply create a single row (or column) Grid and assign the
 * given sizes to each child, positionally. So in the above example,
 * `<ComponentA />` would have a width of `1fr`, `<ComponentB />` would have
 * a width of `3fr` and `<ComponentC />` would have a width of `2fr`.
 *
 * If the number of children in your grid is dynamic, you can use the
 * `<Cell>` component to have each child specify its own size. The parent
 * `<Grid>` component only has to specify that we are a row (or column):
 *
 * @example
 * <Grid row>
 *   <Cell size="1fr"><ComponentA /></Cell>
 *   <Cell size="3fr"><ComponentB /></Cell>
 *   <Cell size="2fr"><ComponentC /></Cell>
 * </Grid>
 *
 * This example is completely equivalent to the previous one.
 *
 */
function Grid(props: GridProps) {
  let gridLayout;
  if (props.layout) {
    // $FlowFixMe - this would be fine if refinement worked properly
    gridLayout = new NamedGridLayout(props.layout, props.children);
  } else if (props.row) {
    gridLayout = new SimpleGridLayout(
      'row',
      // $FlowFixMe - this would be fine if refinement worked properly
      getSimpleLayoutSpec(props.row, props.children),
      props.children,
    );
  } else if (props.column) {
    gridLayout = new SimpleGridLayout(
      'column',
      // $FlowFixMe - this would be fine if refinement worked properly
      getSimpleLayoutSpec(props.column, props.children),
      props.children,
    );
  } else {
    // eslint-disable-next-line no-console
    console.error('Invalid grid properties:', props);
    throw new Error('Invalid grid configuration - see log above prop values.');
  }

  return (
    <StyledGrid
      gridLayout={gridLayout}
      rowSpacing={props.rowSpacing}
      columnSpacing={props.columnSpacing}
    >
      {gridLayout.children}
    </StyledGrid>
  );
}

Grid.defaultProps = {
  rowSpacing: '',
  columnSpacing: '',
};

export default Grid;
